import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import { LinkTo } from '@ember/routing';
import { t } from 'ember-intl';
import { dropTask } from 'ember-concurrency';
import ReimaginedContributionHistory from 'tio-employee/components/reimagined/production/contribution-history';
import LoanStandingAlertCard from 'tio-employee/components/slr/loan-standing-alert-card';
import LoanStandingUploadDrawer from 'tio-employee/components/slr/loan-standing-upload-drawer';
import { shouldShowSlrLoanStandingAlert } from 'tio-employee/utils/slr-loan-standing-verification';
import type SlrLoanStandingVerificationService from 'tio-employee/services/slr-loan-standing-verification';
import type FeaturesService from 'tio-common/services/features';
import type { AuthenticatedSlrDashboardModel } from 'tio-employee/routes/authenticated/slr/dashboard';
import type { SlrLoanStandingAuditState } from 'tio-employee/types/slr-loan-standing-verification';

interface SlrDashboardSignature {
  Args: {
    model: AuthenticatedSlrDashboardModel;
  };
}

export default class SlrDashboardRouteComponent extends Component<SlrDashboardSignature> {
  @service('slr-loan-standing-verification')
  declare slrLoanStandingVerification: SlrLoanStandingVerificationService;
  @service declare features: FeaturesService;

  // Post-upload refresh result, keyed to the model value it superseded. A
  // route refresh (e.g. switching the receiving account re-runs the model
  // hook) replaces the model value, which invalidates this override
  // automatically — a plain tracked copy seeded from args would keep showing
  // the OLD account's alert after the model refreshed underneath it.
  @tracked private refreshedLoanStanding?: {
    supersedes: SlrLoanStandingAuditState | null;
    state: SlrLoanStandingAuditState | null;
  };
  @tracked isLoanStandingUploadDrawerOpen = false;

  get loanStandingVerification() {
    const fromModel = this.args.model.loanStandingVerification;
    const refreshed = this.refreshedLoanStanding;
    return refreshed && refreshed.supersedes === fromModel ? refreshed.state : fromModel;
  }

  get reimaginedModel() {
    return this.args.model.reimagined;
  }

  // TODO: Also hide once the employee is already LRAP-enrolled (needs
  // server-side enrollment state). Flag-gated for now; the route guard
  // redirects here if the flag is off.
  get showLrapEnrollmentLink() {
    return this.features.isEnabled('lrapEnrollment');
  }

  get showLoanStandingAlert() {
    return shouldShowSlrLoanStandingAlert(this.loanStandingVerification);
  }

  openLoanStandingUploadDrawer = () => {
    this.isLoanStandingUploadDrawerOpen = true;
  };

  closeLoanStandingUploadDrawer = () => {
    this.isLoanStandingUploadDrawerOpen = false;
  };

  refreshLoanStandingVerification = dropTask(async () => {
    const supersedes = this.args.model.loanStandingVerification;
    const state = await this.slrLoanStandingVerification.fetchCurrentState();
    this.refreshedLoanStanding = { supersedes, state };
    // The scoped refresh can resolve to null (e.g. the receiving account
    // changed mid-flow); close the upload drawer instead of letting the
    // surrounding {{#if}} unmount it while its open flag is still true.
    if (!state) {
      this.isLoanStandingUploadDrawerOpen = false;
    }
  });

  <template>
    <div class="tio-col-tight">
      <h1 class="app-page-title px-4 pt-4">{{t "dashboard_tiles.student_loan_repayment"}}</h1>
      {{#if this.showLrapEnrollmentLink}}
        <div class="tio-lrap-enrollment-cta">
          <LinkTo
            @route="authenticated.slr.lrap.enrollment"
            class="tio-lrap-enrollment-cta__link"
            data-test-lrap-enrollment-link
          >
            <span class="tio-lrap-enrollment-cta__title">{{t "lrap.enrollment.cta_title"}}</span>
            <span class="tio-lrap-enrollment-cta__body">{{t "lrap.enrollment.cta_body"}}</span>
          </LinkTo>
        </div>
      {{/if}}
      {{#if this.loanStandingVerification}}
        {{#if this.showLoanStandingAlert}}
          <div class="px-4">
            <LoanStandingAlertCard
              @state={{this.loanStandingVerification}}
              @onOpenUpload={{this.openLoanStandingUploadDrawer}}
            />
          </div>
        {{/if}}
      {{/if}}
      <ReimaginedContributionHistory @model={{this.reimaginedModel}} />
    </div>

    {{#if this.loanStandingVerification}}
      <LoanStandingUploadDrawer
        @isOpen={{this.isLoanStandingUploadDrawerOpen}}
        @state={{this.loanStandingVerification}}
        @onClose={{this.closeLoanStandingUploadDrawer}}
        @onRefresh={{this.refreshLoanStandingVerification.perform}}
      />
    {{/if}}
  </template>
}
